import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { adminAction } from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Loader2, Save, AlertTriangle } from "lucide-react";
import { successToast, errorToast } from "@/components/ui/sonner";
import {
  AdminPageHeader,
  AdminPageSkeleton,
  AdminPanel,
  AdminSection,
} from "./AdminUI";

interface SystemSetting {
  key: string;
  value: unknown;
  updated_at: string;
  updated_by: string | null;
}

export function AdminSettingsTab() {
  const queryClient = useQueryClient();
  const [rerankerScore, setRerankerScore] = useState<string>("");
  const [vectorThreshold, setVectorThreshold] = useState<string>("");

  const { data: settings = [], isLoading } = useQuery<SystemSetting[]>({
    queryKey: ["admin", "settings"],
    queryFn: () => adminAction("get_settings"),
    staleTime: 60_000,
  });

  const currentRerankerSetting = settings.find(s => s.key === "reranker_min_score");
  const currentRerankerValue = currentRerankerSetting?.value;
  const displayReranker = rerankerScore || String(currentRerankerValue ?? "4");

  const currentVectorSetting = settings.find(s => s.key === "vector_match_threshold");
  const currentVectorValue = currentVectorSetting?.value;
  const displayVector = vectorThreshold || String(currentVectorValue ?? "0.55");

  const updateMutation = useMutation({
    mutationFn: async (updates: { key: string; value: unknown }[]) => {
      for (const { key, value } of updates) {
        await adminAction("update_setting", { setting_key: key, setting_value: value });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "settings"] });
      successToast("Settings saved", "Generation settings have been updated.");
    },
    onError: (err: Error) => {
      errorToast("Update failed", err.message);
    },
  });

  const handleSave = () => {
    const rerankerNum = parseFloat(displayReranker);
    if (isNaN(rerankerNum) || rerankerNum < 0 || rerankerNum > 10) {
      errorToast("Invalid value", "Reranker score must be between 0 and 10.");
      return;
    }
    const vectorNum = parseFloat(displayVector);
    if (isNaN(vectorNum) || vectorNum < 0 || vectorNum > 1) {
      errorToast("Invalid value", "Similarity threshold must be between 0 and 1.");
      return;
    }
    updateMutation.mutate([
      { key: "reranker_min_score", value: rerankerNum },
      { key: "vector_match_threshold", value: vectorNum },
    ]);
  };

  if (isLoading) return <AdminPageSkeleton stats={0} rows={3} columns={2} />;

  const lastUpdated = (s?: SystemSetting) =>
    s?.updated_at
      ? new Date(s.updated_at).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : "Never";

  return (
    <div className="space-y-10">
      <AdminPageHeader
        eyebrow="Platform"
        title="Generation settings"
        description="Tune the relevance and similarity thresholds that govern how Sorvaine selects vault content for every proposal."
        meta={`${settings.length} settings · Live`}
      />

      <div
        className="flex items-start gap-3 rounded-[4px] px-5 py-4"
        style={{
          background: "rgba(217,119,6,0.06)",
          border: "1px solid rgba(217,119,6,0.18)",
        }}
      >
        <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 flex-shrink-0" />
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-amber-700">
            Platform-wide effect
          </p>
          <p className="text-[12.5px] text-muted-foreground mt-1 leading-relaxed max-w-2xl">
            Changes here apply to every organisation on Sorvaine instantly.
            Validate adjustments against a single proposal before broad tuning.
          </p>
        </div>
      </div>

      <AdminSection
        title="Retrieval thresholds"
        description="Two dials that determine which vault entries become candidates for AI generation."
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <SettingPanel
            label="Minimum relevance"
            scale="0 — 10"
            description="How relevant vault content must be before it's used. Raise to tighten quality; lower if proposals feel thin."
            value={displayReranker}
            onChange={setRerankerScore}
            min={0}
            max={10}
            step={0.5}
            currentValue={String(currentRerankerValue ?? "4")}
            defaultValue="4"
            updated={lastUpdated(currentRerankerSetting)}
          />
          <SettingPanel
            label="Vector similarity"
            scale="0 — 1"
            description="How similar a vault entry must be to a tender question to be considered. Higher = stricter matching."
            value={displayVector}
            onChange={setVectorThreshold}
            min={0}
            max={1}
            step={0.05}
            currentValue={String(currentVectorValue ?? "0.55")}
            defaultValue="0.55"
            updated={lastUpdated(currentVectorSetting)}
          />
        </div>

        <div className="flex justify-end pt-2">
          <Button
            onClick={handleSave}
            disabled={updateMutation.isPending}
            className="h-9 px-5 text-[12px] uppercase tracking-[0.18em] rounded-[3px]"
            style={{ background: "#c2541a" }}
          >
            {updateMutation.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" />
            ) : (
              <Save className="w-3.5 h-3.5 mr-2" />
            )}
            Save changes
          </Button>
        </div>
      </AdminSection>
    </div>
  );
}

function SettingPanel({
  label,
  scale,
  description,
  value,
  onChange,
  min,
  max,
  step,
  currentValue,
  defaultValue,
  updated,
}: {
  label: string;
  scale: string;
  description: string;
  value: string;
  onChange: (v: string) => void;
  min: number;
  max: number;
  step: number;
  currentValue: string;
  defaultValue: string;
  updated: string;
}) {
  return (
    <AdminPanel padded className="space-y-5">
      <div>
        <div className="flex items-baseline justify-between gap-3">
          <h3
            className="text-[20px] text-foreground"
            style={{
              fontFamily: "Outfit, sans-serif",
              fontWeight: 300,
              letterSpacing: "-0.01em",
            }}
          >
            {label}
          </h3>
          <span className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
            {scale}
          </span>
        </div>
        <p className="text-[12.5px] text-muted-foreground mt-2 leading-relaxed">
          {description}
        </p>
      </div>

      <div className="flex items-end gap-4">
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-12 w-28 rounded-[3px] px-3 text-[22px] tabular-nums text-foreground focus:outline-none focus:border-[#c2541a] focus:ring-1 focus:ring-[rgba(194,84,26,0.25)]"
          style={{
            background: "#faf7f1",
            border: "1px solid rgba(58,53,48,0.14)",
            fontFamily: "Outfit, sans-serif",
            fontWeight: 300,
            letterSpacing: "-0.01em",
          }}
        />
        <div className="text-[11px] text-muted-foreground pb-2 leading-relaxed">
          <div>
            Current{" "}
            <span className="text-foreground tabular-nums">{currentValue}</span>
            <span className="mx-2 text-muted-foreground/40">·</span>
            Default <span className="tabular-nums">{defaultValue}</span>
          </div>
          <div className="text-muted-foreground/70 mt-0.5">
            Updated {updated}
          </div>
        </div>
      </div>
    </AdminPanel>
  );
}
